import {
  type Action,
  type Transfer,
  checkIfActionsIsValid,
} from '@0xtorch/core'
import type { LowerHex } from '../../../types'
import type { TransactionDecoded } from '../../../types/transactionDecoded'
import { containsUrlOrDomain } from './utils/containsUrlOrDomain'

type CreateActionsBySpamAnalyzerParameters = {
  readonly transaction: TransactionDecoded
  readonly transfers: readonly Transfer[]
  readonly relatedAddresses: Set<LowerHex>
  readonly accountIds: Set<string>
  readonly source: string
}

export const createActionsBySpamAnalyzer = ({
  transaction,
  transfers,
  relatedAddresses,
  accountIds,
  source,
}: CreateActionsBySpamAnalyzerParameters): readonly Action[] | undefined => {
  // related addresses に transaction.from が含まれている場合、 spam とは判定しない
  if (relatedAddresses.has(transaction.from.address)) {
    return undefined
  }

  let existSpam = false
  const actions: Action[] = []

  // from address が spam の場合、時価総額が存在しないトークンを spam として action 生成
  // spam token の transfer は spam action 生成
  // spam token ではない transfer は income/transfer/move action 生成
  for (const transfer of transfers) {
    if (transfer.direction === 'none') {
      actions.push({
        type: 'move',
        timestamp: transaction.timestamp,
        source,
        order: 0,
        evidence: 'system-rule',
        transfers: [transfer],
      })
      continue
    }

    switch (transfer.asset.type) {
      case 'CryptoCurrency': {
        const isSpam =
          (transfer.asset.market?.marketCapUsd === undefined ||
            transfer.asset.market.marketCapUsd === 0) &&
          (transaction.from.isSpam === true ||
            containsUrlOrDomain(transfer.asset.symbol))
        if (isSpam) {
          existSpam = true
          actions.push({
            type: 'spam',
            timestamp: transaction.timestamp,
            source,
            order: 0,
            evidence: 'system-rule',
            transfers: [transfer],
          })
        } else if (transfer.direction === 'in') {
          actions.push({
            type: 'gift',
            timestamp: transaction.timestamp,
            source,
            order: 0,
            evidence: 'none',
            transfers: [transfer],
          })
        } else {
          actions.push({
            type: 'transfer',
            timestamp: transaction.timestamp,
            source,
            order: 0,
            evidence: 'none',
            transfers: [transfer],
          })
        }
        break
      }
      case 'FiatCurrency': {
        if (transfer.direction === 'in') {
          actions.push({
            type: 'gift',
            timestamp: transaction.timestamp,
            source,
            order: 0,
            evidence: 'none',
            transfers: [transfer],
          })
        } else {
          actions.push({
            type: 'transfer',
            timestamp: transaction.timestamp,
            source,
            order: 0,
            evidence: 'none',
            transfers: [transfer],
          })
        }
        break
      }
      case 'Nft': {
        const isSpam =
          transaction.from.isSpam === true ||
          containsUrlOrDomain(transfer.asset.name ?? '')
        if (isSpam) {
          existSpam = true
          actions.push({
            type: 'spam',
            timestamp: transaction.timestamp,
            source,
            order: 0,
            evidence: 'system-rule',
            transfers: [transfer],
          })
        } else if (transfer.direction === 'in') {
          actions.push({
            type: 'gift',
            timestamp: transaction.timestamp,
            source,
            order: 0,
            evidence: 'none',
            transfers: [transfer],
          })
        } else {
          actions.push({
            type: 'transfer',
            timestamp: transaction.timestamp,
            source,
            order: 0,
            evidence: 'none',
            transfers: [transfer],
          })
        }
        break
      }
      default: {
        throw new Error(`Unknown asset type: ${transfer.asset satisfies never}`)
      }
    }
  }

  // spam を含む場合は validation して問題なければ actions を返す
  // 含まない場合は undefined を返す
  if (!existSpam) {
    return undefined
  }
  if (!checkIfActionsIsValid({ actions, accountIds, transfers })) {
    return undefined
  }
  return actions
}
